import React, { useState } from 'react';
import { VisualAssetEditor, VisualAssetData } from '@/components/VisualAssetEditor';
import { Button } from '@/components/ui/button';
import { Edit3, Download, Eye, Trash2, Palette } from 'lucide-react';

interface AssetData extends VisualAssetData {
  id: string;
  createdAt: Date;
}

const AspectWeaveKitDemo: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [savedAssets, setSavedAssets] = useState<AssetData[]>([]);
  const [editingAsset, setEditingAsset] = useState<AssetData | null>(null);

  const handleSave = (data: VisualAssetData) => {
    const assetData: AssetData = {
      id: editingAsset?.id || `asset-${Date.now()}`,
      ...data,
      createdAt: editingAsset?.createdAt || new Date()
    };

    if (editingAsset) {
      // Update existing asset
      setSavedAssets(prev => prev.map(asset => 
        asset.id === editingAsset.id ? assetData : asset
      ));
    } else {
      // Create new asset
      setSavedAssets(prev => [...prev, assetData]);
    }

    setEditingAsset(null);
  };

  const handleNewAsset = () => {
    setEditingAsset(null);
    setIsOpen(true);
  };

  const handleEditAsset = (asset: AssetData) => {
    setEditingAsset(asset);
    setIsOpen(true);
  };

  const handleDeleteAsset = (assetId: string) => {
    setSavedAssets(prev => prev.filter(asset => asset.id !== assetId));
  };

  const handleDownloadAsset = (asset: AssetData) => {
    // Mock download functionality
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Fill background with white
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 800, 600);
      
      // Draw header text
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 32px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(asset.header, 400, 250);
      
      // Draw subheader if exists
      if (asset.subheader) {
        ctx.font = '24px Arial';
        ctx.fillText(asset.subheader, 400, 290);
      }
      
      // Draw company name
      ctx.font = '20px Arial';
      ctx.fillText(asset.companyName, 400, 350);
      
      // Create download link
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${asset.header.toLowerCase().replace(/\s+/g, '-')}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Palette className="h-8 w-8 text-purple-600" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Aspect Weave Kit Demo
            </h1>
          </div>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Interactive demo of the Visual Asset Editor from the aspect-weave-kit submodule. 
            Create, customize, and manage visual assets with live preview and advanced editing capabilities.
          </p>
          <div className="mt-4 p-4 bg-purple-100 rounded-lg border border-purple-200">
            <p className="text-purple-800 font-medium">
              🎨 Now using direct submodule integration • Latest features • Custom styling applied
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8 flex flex-wrap gap-4 justify-center">
          <Button 
            onClick={handleNewAsset} 
            className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            size="lg"
          >
            <Edit3 className="h-5 w-5 mr-2" />
            Create New Visual Asset
          </Button>
        </div>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Assets</p>
                <p className="text-3xl font-bold text-gray-900">{savedAssets.length}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Edit3 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Recent Assets (24h)</p>
                <p className="text-3xl font-bold text-gray-900">
                  {savedAssets.filter(asset => 
                    Date.now() - asset.createdAt.getTime() < 24 * 60 * 60 * 1000
                  ).length}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Downloads</p>
                <p className="text-3xl font-bold text-gray-900">0</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Download className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Kit Version</p>
                <p className="text-lg font-bold text-gray-900">Submodule</p>
              </div>
              <div className="h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Palette className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Assets Grid */}
        {savedAssets.length > 0 ? (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Edit3 className="h-6 w-6 text-purple-600" />
              Your Visual Assets
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {savedAssets.map((asset) => (
                <div key={asset.id} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  {/* Asset Preview */}
                  <div className="h-48 p-6 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                    <h3 className="font-bold mb-2 text-center text-gray-900 text-lg">
                      {asset.header}
                    </h3>
                    {asset.subheader && (
                      <p className="text-gray-700 text-sm mb-2 text-center">
                        {asset.subheader}
                      </p>
                    )}
                    <p className="text-gray-600 text-sm text-center">
                      {asset.companyName}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                        {asset.aspectRatio}
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {asset.viewMode}
                      </span>
                    </div>
                  </div>

                  {/* Asset Details */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900 truncate">
                          {asset.header}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {asset.companyName}
                        </p>
                        {asset.subheader && (
                          <p className="text-xs text-gray-400">
                            {asset.subheader}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">
                        {asset.createdAt.toLocaleDateString()}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditAsset(asset)}
                        className="flex-1 hover:bg-purple-50 hover:border-purple-300"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadAsset(asset)}
                        className="hover:bg-blue-50 hover:border-blue-300"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteAsset(asset.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md mx-auto">
              <Palette className="h-16 w-16 text-purple-400 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Ready to Create?</h3>
              <p className="text-gray-600 mb-8">
                Start building your first visual asset with the aspect-weave-kit editor. 
                Unleash your creativity with our powerful tools!
              </p>
              <Button 
                onClick={handleNewAsset}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                size="lg"
              >
                <Edit3 className="h-5 w-5 mr-2" />
                Create Your First Asset
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Visual Asset Editor Modal */}
      <VisualAssetEditor
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setEditingAsset(null);
        }}
        onSave={handleSave}
        initialData={editingAsset ? {
          aspectRatio: editingAsset.aspectRatio,
          viewMode: editingAsset.viewMode,
          header: editingAsset.header,
          subheader: editingAsset.subheader,
          companyName: editingAsset.companyName,
          companyLogo: editingAsset.companyLogo,
          images: editingAsset.images,
          customData: editingAsset.customData
        } : {
          aspectRatio: '16:9',
          viewMode: 'single',
          header: 'Your Awesome Header',
          subheader: 'Powered by Aspect Weave Kit',
          companyName: 'Your Company'
        }}
      />
    </div>
  );
};

export default AspectWeaveKitDemo;